import type { ReactNode } from 'react';
import { Box, Typography, type SxProps, type Theme } from '@mui/material';
import { osirisTokens } from '@/theme/tokens';

type CalloutState = 'info' | 'accent' | 'success' | 'warning' | 'error';

interface CalloutProps {
  children: ReactNode;
  /** Optional 20px leading icon */
  icon?: ReactNode;
  state?: CalloutState;
  className?: string;
  sx?: SxProps<Theme>;
}

/**
 * Figma Callout component (28400:20699): bg-secondary fill, 2px radius,
 * 16×12 padding, IBM Plex Mono 11 txt-secondary; the accent state uses a
 * 15% accent fill with txt-active text.
 *
 * The severity fills match the MuiAlert theme override so ad-hoc <Alert>
 * hints and Callouts read as one component.
 */
const STATE_STYLES: Record<CalloutState, { bgcolor: string; color: string }> = {
  info: { bgcolor: osirisTokens.bg.secondary, color: osirisTokens.text.secondary },
  // Both accent instances (28711:30886 "No rules", 28044:46673 "No commands")
  // put txt-secondary on the 15% accent fill, not txt-active
  accent: { bgcolor: 'rgba(110, 68, 245, 0.15)', color: osirisTokens.text.secondary },
  success: { bgcolor: 'rgba(116, 200, 124, 0.15)', color: osirisTokens.text.success },
  warning: { bgcolor: 'rgba(255, 204, 0, 0.15)', color: osirisTokens.text.warning },
  error: { bgcolor: 'rgba(250, 121, 101, 0.15)', color: osirisTokens.text.error },
};

export function Callout({ children, icon, state = 'info', className, sx }: CalloutProps) {
  const { bgcolor, color } = STATE_STYLES[state];
  return (
    <Box
      className={`callout callout-${state}${className ? ` ${className}` : ''}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.5,
        minHeight: 40,
        borderRadius: '2px',
        bgcolor,
        ...sx,
      }}
    >
      {icon}
      <Typography
        component="div"
        sx={{
          flex: 1,
          fontFamily: osirisTokens.fonts.mono,
          fontSize: 11,
          letterSpacing: '0.02em',
          lineHeight: 1.5,
          color,
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}
